import type { WalletDto } from '@/queries/wallet/wallet.schema';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { useCurrency } from '@/hooks/useCurrency';
import { useIsMobile } from '@/hooks/useIsMobile';
import { summarizeSCBalances } from '@/libs/summarizeSCBalances';
import openModal from '@/modals/utils/openModal';
import goldCoinShine from '@/public/assets/image/balance/gold-coin-shine.webp';
import sweepCoinShine from '@/public/assets/image/balance/sweep-coin-shine.webp';
import { cn } from '@/utils/cn';
import { formatCentsToCurrencyWithDecimals } from '@/utils/currency.utils';
import BalanceInfoModal from './BalanceInfoModal';
import CurrencyOptionButton from './CurrencyOptionButton';

interface Props {
  coins?: WalletDto;
  balancesPopoverClose: () => void;
}
const BalanceCard = ({ coins, balancesPopoverClose }: Props) => {
  const t = useTranslations('header.balance-card');
  const { currency, setCurrency } = useCurrency();
  const [isBalanceInfoOpen, setIsBalanceInfoOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleSelectCurrency = (currency: 'GC' | 'SC') => {
    setCurrency(currency);
    balancesPopoverClose();
  };

  const handleOpenDepositModal = () => {
    openModal('deposit');
    balancesPopoverClose();
  };

  const handleInfoClick = () => {
    if (isMobile) {
      setIsBalanceInfoOpen(true);
    } else {
      openModal('sweep-coin-info');
    }
  };

  return (
    <>
      <div className="flex relative items-center justify-between">
        <h6 className="text-c300 paragraph-s-medium px-2">{t('choose-currency')}</h6>
        <div className="pr-2">
          <button
            type="button"
            className="size-8 flex items-center justify-center rounded-full  bg-black/30 hover:bg-violet-main-900 transition-colors duration-300"
            aria-label="Close"
            onClick={balancesPopoverClose}
          >
            <Icon name="x_close" className="text-c200 size-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-col mt-4 gap-2 px-2">
        <CurrencyOptionButton
          isActive={currency === 'GC'}
          iconName="gold-coin"
          shineSrc={goldCoinShine}
          shineAlt="Gold Coin Shine"
          shineIdlePosition="left-[90%] top-[50%] scale-330"
          shineActivePosition="left-full top-[60%] scale-400"
          transitionDuration="duration-300"
          label={t('gold-coins')}
          value={formatCentsToCurrencyWithDecimals(coins?.goldCoin || 0)}
          onSelect={() => handleSelectCurrency('GC')}
        />
        <CurrencyOptionButton
          isActive={currency === 'SC'}
          iconName="sweep-coin"
          shineSrc={sweepCoinShine}
          shineAlt="Sweep Coin Shine"
          shineIdlePosition="left-[90%] top-[-30%] scale-330"
          shineActivePosition="left-full top-[-40%] scale-400"
          transitionDuration="duration-400"
          label={t('sweeps-coins')}
          labelSuffix={(
            <div
              tabIndex={0}
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInfoClick();
                }
              }}
              className="group cursor-pointer transition-colors duration-300"
            >
              <Icon name="info" className={cn('size-3 transition-colors duration-300 group-hover:text-c300', currency === 'SC' ? 'text-white' : 'text-white/80')} />
            </div>
          )}
          value={formatCentsToCurrencyWithDecimals(summarizeSCBalances(Number(coins?.sweepCoin), Number(coins?.bonusSweepCoin)))}
          onSelect={() => handleSelectCurrency('SC')}
        />
      </div>
      <div className="px-2">
        <Button
          variant="primary"
          size="sm"
          className="paragraph-s-medium w-full pl-[6.6563rem] mt-4 h-11 pr-[7.1563rem] py-3 text-white"
          onClick={handleOpenDepositModal}
        >
          <div className="flex items-center ml-2 gap-1">
            <Icon name="top-up" className="size-4 text-white" />
            <span className="">{t('top-up')}</span>
          </div>
        </Button>
      </div>
      <div className="pointer-events-none absolute -right-36.5 -bottom-22.5 blur-3xl w-[17.2087rem] h-[8.4281rem] rounded-[624.9375rem] bg-frame-gradient rotate-[162.88deg]" />
      <BalanceInfoModal open={isBalanceInfoOpen} onOpenChange={setIsBalanceInfoOpen} />
    </>
  );
};

export default BalanceCard;
